/*
 * <sticky-atc> — mobile sticky add-to-cart (Option E: size selector + button).
 *
 * It does NOT re-implement add-to-cart. It proxies the real product form button
 * (#ProductSubmitButton-<section>), so all of Dawn's variant / loading / cart-drawer
 * logic is reused. Dawn mutates that button in place on variant change (toggles
 * `disabled`, swaps the <span> text), so the reference and observers stay valid.
 */
if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      connectedCallback() {
        // The quick-add modal transplants the product page markup, so this
        // component arrives with it. It has no job there — the modal already
        // has its own size selector and add-to-cart — and it cannot position
        // correctly inside a transformed, backdrop-filtered container. CSS
        // hides it; bail out here too so no observers watch a hidden bar.
        if (this.closest('quick-add-modal, .quick-add-modal')) return;

        this.sectionId = this.dataset.sectionId;
        this.productInfo = this.closest('product-info');
        this.realButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.proxyButton = this.querySelector('[data-sticky-atc-button]');
        this.proxyLabel = this.proxyButton && this.proxyButton.querySelector('span');
        this.select = this.querySelector('[data-sticky-atc-select]');
        this.suppress = false;

        if (!this.realButton || !this.proxyButton) return;

        // Proxy the click straight through to the real add-to-cart button.
        this.proxyButton.addEventListener('click', () => {
          if (this.proxyButton.disabled) return;
          this.realButton.click();
        });

        // Mirror disabled state + label text from the real button.
        this.syncFromRealButton();
        this.buttonObserver = new MutationObserver(() => this.syncFromRealButton());
        this.buttonObserver.observe(this.realButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled'],
          childList: true,
          subtree: true,
        });

        // Size selector drives the on-page variant picker, and vice-versa.
        if (this.select && this.productInfo) {
          this.select.addEventListener('change', () => this.onSelectChange());
          // change events bubble; <variant-selects> may be re-rendered, so delegate
          // off the stable <product-info> root instead of caching the picker node.
          this.productInfo.addEventListener('change', (event) => {
            if (this.suppress) return;
            if (event.target.closest && event.target.closest('variant-selects')) {
              this.syncSelectFromPicker();
            }
          });
        }

        // Show the bar once the real button scrolls out of view; hide when back.
        this.observer = new IntersectionObserver(
          (entries) => this.classList.toggle('is-visible', !entries[0].isIntersecting),
          { threshold: 0 }
        );
        this.observer.observe(this.realButton);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.buttonObserver) this.buttonObserver.disconnect();
      }

      syncFromRealButton() {
        const disabled =
          this.realButton.hasAttribute('disabled') ||
          this.realButton.getAttribute('aria-disabled') === 'true';
        this.proxyButton.disabled = disabled;
        const realLabel = this.realButton.querySelector('span');
        if (realLabel && this.proxyLabel) {
          this.proxyLabel.textContent = realLabel.textContent.trim();
        }
      }

      get picker() {
        return document.getElementById(`variant-selects-${this.sectionId}`);
      }

      onSelectChange() {
        const picker = this.picker;
        if (!picker) return;
        const value = this.select.value;

        const realSelect = picker.querySelector('select');
        if (realSelect) {
          realSelect.value = value;
          realSelect.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }

        const safe = window.CSS && CSS.escape ? CSS.escape(value) : value.replace(/"/g, '\\"');
        const radio = picker.querySelector(`input[type="radio"][value="${safe}"]`);
        if (radio && !radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      syncSelectFromPicker() {
        if (!this.select) return;
        const picker = this.picker;
        if (!picker) return;

        let current = null;
        const realSelect = picker.querySelector('select');
        if (realSelect) {
          current = realSelect.value;
        } else {
          const checked = picker.querySelector('input[type="radio"]:checked');
          current = checked ? checked.value : null;
        }

        if (current != null && this.select.value !== current) {
          this.suppress = true;
          this.select.value = current;
          this.suppress = false;
        }
      }
    }
  );
}
